import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import React, { useState } from 'react';
import SingleInput from '../CarLoanCalculator/SingleInput';
import { InputField, isNumber } from '../CarLoanCalculator/helpers';
import clsx from 'clsx';
import '../CarLoanCalculator/style.scss';
import './style.scss';
import { withTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const DebtBurdenRatioCalculator = ({ fields = {}, t, params }) => {
  // main
  const { title, description, currency } = fields;
  // monthly salary
  const {
    monthlySalaryLabel,
    monthlySalarySubLabel,
    monthlySalaryMin,
    monthlySalaryMax,
    monthlySalaryMaxLength,
    monthlySalaryPlaceholder,
    monthlySalaryStep,
    monthlySalaryAdornmentText,
  } = fields;
  // personal loan
  const {
    personalLoanLabel,
    personalLoanSubLabel,
    personalLoanMin,
    personalLoanMax,
    personalLoanMaxLength,
    personalLoanPlaceholder,
    personalLoanStep,
    personalLoanAdornmentText,
  } = fields;
  // car loan
  const {
    carLoanLabel,
    carLoanSubLabel,
    carLoanMin,
    carLoanMax,
    carLoanMaxLength,
    carLoanPlaceholder,
    carLoanStep,
    carLoanAdornmentText,
    creditCardPercentage,
  } = fields;
  // mortage
  const {
    mortgageLabel,
    mortgageSubLabel,
    mortgageMin,
    mortgageMax,
    mortgageMaxLength,
    mortgagePlaceholder,
    mortgageStep,
    mortgageAdornmentText,
  } = fields;
  // credit card
  const {
    creditCardLabel,
    creditCardSubLabel,
    creditCardMin,
    creditCardMax,
    creditCardMaxLength,
    creditCardPlaceholder,
    creditCardStep,
    creditCardAdornmentText,
  } = fields;
  // right-card
  const {
    additionalNote,
    additionalText,
    recalculateButtonLabel,
    calculateButtonLabel,
    resultRemainingLabel,
    aboveDBRPercentage,
    belowDBRPercentage,
    equalDBRPercentage,
    dBRPercentage,
    aboveDBRPercentageWarning,
  } = fields;

  const defaultValue = {
    value: 0,
  };

  //main
  const shouldRenderTitle = !!title?.value;
  const shouldRenderDescription = !!description?.value;
  const shouldRenderCurrency = !!currency?.value;
  // monthly salary
  const monthlySalaryProps = new InputField(
    monthlySalaryLabel,
    monthlySalarySubLabel,
    defaultValue,
    monthlySalaryMin,
    monthlySalaryMax,
    monthlySalaryMaxLength,
    monthlySalaryPlaceholder,
    monthlySalaryStep,
    monthlySalaryAdornmentText
  );
  // personal loan
  const personalLoanProps = new InputField(
    personalLoanLabel,
    personalLoanSubLabel,
    defaultValue,
    personalLoanMin,
    personalLoanMax,
    personalLoanMaxLength,
    personalLoanPlaceholder,
    personalLoanStep,
    personalLoanAdornmentText
  );
  // car loan
  const carLoanProps = new InputField(
    carLoanLabel,
    carLoanSubLabel,
    defaultValue,
    carLoanMin,
    carLoanMax,
    carLoanMaxLength,
    carLoanPlaceholder,
    carLoanStep,
    carLoanAdornmentText
  );
  // mortgage
  const mortgageProps = new InputField(
    mortgageLabel,
    mortgageSubLabel,
    defaultValue,
    mortgageMin,
    mortgageMax,
    mortgageMaxLength,
    mortgagePlaceholder,
    mortgageStep,
    mortgageAdornmentText
  );
  // credit card
  const creditCardProps = new InputField(
    creditCardLabel,
    creditCardSubLabel,
    defaultValue,
    creditCardMin,
    creditCardMax,
    creditCardMaxLength,
    creditCardPlaceholder,
    creditCardStep,
    creditCardAdornmentText
  );
  //right-card
  const shouldRenderAdditionalNote = !!additionalNote?.value;
  const shouldRenderAdditionalText = !!additionalText?.value;
  const shouldRendercalculateButtonLabel = !!calculateButtonLabel?.value;
  const shouldRenderRecalculateButtonLabel = !!recalculateButtonLabel?.value;
  const shouldRenderResultRemainingLabel = !!resultRemainingLabel?.value;
  const shouldRenderAboveDBRPercentage = !!aboveDBRPercentage?.value;
  const shouldRenderBelowDBRPercentage = !!belowDBRPercentage?.value;
  const shouldRenderEqualDBRPercentage = !!equalDBRPercentage?.value;
  const shouldRenderAboveDBRPercentageWarning = !!aboveDBRPercentageWarning?.value;

  // Input states
  const [monthlySalary, setMonthlySalary] = useState(defaultValue?.value);
  const [personalLoan, setPersonalLoan] = useState(defaultValue?.value);
  const [carLoan, setCarLoan] = useState(defaultValue?.value);
  const [mortgage, setMortgage] = useState(defaultValue?.value);
  const [creditCard, setCreditCard] = useState(defaultValue?.value);

  // result state
  const [percentage, setPercentage] = useState(defaultValue?.value);
  const [remaining, setRemaining] = useState(defaultValue?.value);

  // first calculate or not state
  const [firstCalculate, setFirstCalculate] = useState(false);

  function caclulateResults() {
    if (
      !isNumber(monthlySalary) ||
      !isNumber(personalLoan) ||
      !isNumber(carLoan) ||
      !isNumber(mortgage) ||
      !isNumber(creditCard)
    ) {
      return;
    }
    if (monthlySalary === 0) {
      return;
    }
    if (!firstCalculate) {
      setFirstCalculate(true);
    }
    const credit = creditCard * (creditCardPercentage?.value || 0.05);
    const diffRes = monthlySalary - personalLoan - carLoan - mortgage - credit;
    const diffWithPercentageRes = diffRes;
    const res = Math.round(((monthlySalary - diffWithPercentageRes) / monthlySalary) * 100);
    setPercentage(res);
    setRemaining(diffRes);
  }

  const renderResInfo = () => {
    if (!firstCalculate) {
      return;
    }
    if (percentage < dBRPercentage?.value) {
      return (
        shouldRenderBelowDBRPercentage && (
          <RichText className="res-info" field={belowDBRPercentage} />
        )
      );
    } else if (percentage > dBRPercentage?.value) {
      return (
        <>
          {shouldRenderAboveDBRPercentageWarning && (
            <RichText className="res-warning" field={aboveDBRPercentageWarning} />
          )}
          {shouldRenderAboveDBRPercentage && (
            <RichText className="res-info" field={aboveDBRPercentage} />
          )}
        </>
      );
    } else {
      return (
        shouldRenderEqualDBRPercentage && (
          <RichText className="res-info" field={equalDBRPercentage} />
        )
      );
    }
  };

  const options = {
    chart: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      className: 'highchart-custom',
      height: 258,
      width: 258,
      spacing: [0, 0, 0, 0],
      selectionMarkerFill: 'none',
    },
    title: {
      text: `${percentage}%`,
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#FFFFFF',
        fontSize: '36px',
        fontWeight: 600,
        lineHeight: '19px',
        fontFamily: 'Graphik',
      },
      margin: 0,
    },
    exporting: {
      enabled: false,
    },
    accessibility: {
      point: {
        valueSuffix: '%',
      },
    },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: false,
          tooltip: {
            visible: false,
          },
          style: {
            fontWeight: 'bold',
            color: 'white',
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    series: [
      {
        type: 'pie',
        innerSize: '50%',
        borderWidth: 0,
        data: [
          {
            color: '#ffffff73',
            name: 'percent',
            y: 100 - percentage,
          },
          {
            color: 'white',
            name: 'non-percent',
            y: percentage,
          },
        ],
      },
    ],
  };

  return (
    <div className={clsx('debt-burden-calculator content', 'add-margins', params?.Styles)}>
      {(shouldRenderTitle || shouldRenderDescription) && (
        <div className="car-loan-heading">
          {shouldRenderTitle && (
            <h3>
              <Text field={title} />
            </h3>
          )}
          {shouldRenderDescription && (
            <RichText className="car-loan-heading_description" field={description} />
          )}
        </div>
      )}
      <div className="car-loan-calculator-content-container">
        <div className="content-container-left">
          <SingleInput
            t={t}
            {...monthlySalaryProps}
            value={monthlySalary}
            setValue={setMonthlySalary}
          />
          <SingleInput
            t={t}
            {...personalLoanProps}
            value={personalLoan}
            setValue={setPersonalLoan}
          />
          <SingleInput t={t} {...carLoanProps} value={carLoan} setValue={setCarLoan} />
          <SingleInput t={t} {...mortgageProps} value={mortgage} setValue={setMortgage} />
          <SingleInput t={t} {...creditCardProps} value={creditCard} setValue={setCreditCard} />
        </div>
        <div className="content-container-right">
          <div className="result-card">
            {firstCalculate && (
              <HighchartsReact
                highcharts={Highcharts}
                constructorType={'chart'}
                options={options}
              />
            )}
            {renderResInfo()}
            {shouldRenderResultRemainingLabel && firstCalculate && (
              <>
                <div className="monthly__repayment--label result-label">
                  <Text field={resultRemainingLabel} />
                </div>
                <div className="monthly__repayment--value result-value">
                  {shouldRenderCurrency && (
                    <span>
                      <Text field={currency} />
                    </span>
                  )}
                  <div>{typeof remaining === 'number' && remaining.toLocaleString()}</div>
                </div>
              </>
            )}
            {shouldRenderAdditionalNote && firstCalculate && (
              <RichText className="note" field={additionalNote} />
            )}
            {firstCalculate
              ? shouldRenderRecalculateButtonLabel && (
                  <button
                    onClick={caclulateResults}
                    className="button button-contained-secondary btn-schedule"
                  >
                    {recalculateButtonLabel.value}
                  </button>
                )
              : shouldRendercalculateButtonLabel && (
                  <button
                    onClick={caclulateResults}
                    className="button button-contained-secondary btn-schedule"
                  >
                    {calculateButtonLabel.value}
                  </button>
                )}
          </div>
          {shouldRenderAdditionalText && (
            <RichText field={additionalText} className="additional-note" />
          )}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(DebtBurdenRatioCalculator);
